// Bridges a form layout's schema onto a create_record value bag. The two do not share a key space:
// a layout addresses standard fields by PLATFORM name (`userId`, `funnelId`, `dealStageId`) while a
// create_record bag is keyed by field NAME (`user`, `funnel`, `stage`). Reading a bag with a
// platform name finds nothing, which reads as "required field still empty" forever.

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import {
  ChatCreatableEntityType,
  ChatCustomFieldValueRaw,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { isRecord } from "@mapmycustomers/shared/util/chat/chatWireGuards";

import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import getChildLayout from "@app/util/layout/getChildLayout";
import getLayoutModelByEntityType from "@app/util/layout/impl";

import { resolveStage } from "./proposalStage";

export interface CreateRecordSchemaField {
  // Absent when the org's layout references a field this build's model does not define. Everything
  // else about the entry — whether it is custom, the layout's own key — is read off it.
  field?: IField;
  key: string;
  required: boolean;
  // False for a layout entry that carries no value of its own — a divider, a files or notes block.
  // Those can still be flagged required by a layout, and nothing can ever fill them.
  valued: boolean;
}

// The platform never enforces a required owner — it defaults an absent one to the requesting user
// and filters userId out of the required set outright (getFilteredRequiredFields in
// server/services/validators/layout-validator.js, mmc-server 4353ed102: "we do not need to check
// required validation for userId as it can never be null"). Enforcing it here only forces
// the rep to open the editor and press Save to copy in a value the platform would have written.
const OWNER_PLATFORM_NAME = "userId";

// A deal's loss reason and comment, as a deal layout names them.
const LOSS_PLATFORM_NAMES = ["dealLossReasonId", "dealLossComment"];

// The schema entry types that address a value the card can draw. FILE and GROUP are two: `files`
// (a list of ids) and `groups` are real create payload keys, and the layout row is the ONLY way an
// attachment reaches the card — a proposal never names files. DIVIDER, NOTE and AUXILIARY are layout
// furniture: they render something, but there is no key on a create payload for them.
const VALUED_SCHEMA_FIELD_TYPES: string[] = [
  SchemaFieldType.STANDARD,
  SchemaFieldType.CUSTOM,
  SchemaFieldType.FILE,
  SchemaFieldType.GROUP,
];

// Every field a create_record value bag names: the flat standard keys, plus the esKey of each
// nested custom field. Which fields a bag NAMES is a different question from which the layout
// carries, and both the card's rows and the editor's added-field list are built on it — so they
// read the same answer rather than two that can drift.
export const valueKeys = (bag: Record<string, unknown>): string[] => [
  ...Object.keys(bag).filter((key) => key !== "customFields"),
  ...(Array.isArray(bag.customFields)
    ? (bag.customFields as ChatCustomFieldValueRaw[]).map(({ esKey }) => esKey)
    : []),
];

export const readIdOf = (value: unknown): number | undefined =>
  isRecord(value) && typeof value.id === "number" ? value.id : undefined;

// The layout the rep picked, else the org default. `undefined` means layouts are not loaded, which
// callers must treat as "cannot judge" rather than "nothing is required".
export const resolveCreateRecordSchema = (
  entityType: ChatCreatableEntityType,
  values: Record<string, unknown>,
  layoutId: number | undefined,
  // Only to tell a lost stage apart; an unknown stage counts as not lost.
  funnelStages: Record<Funnel["id"], Stage[]> = {}
): CreateRecordSchemaField[] | undefined => {
  const layoutModel = getLayoutModelByEntityType(entityType);
  const layout =
    layoutId !== undefined ? layoutModel.getById(layoutId) : layoutModel.defaultFormLayout;
  if (!layout) {
    return undefined;
  }

  // funnel/stage are wire-shaped { id } here, the same shape proposedValues carries; the pair
  // selects a child layout, whose schema differs from the parent's.
  const funnelId = readIdOf(values.funnel);
  const stageId = readIdOf(values.stage);
  const fieldModel = getFieldModelByEntityType(entityType);
  const childLayout = getChildLayout(
    layout,
    funnelId !== undefined && stageId !== undefined ? { funnelId, stageId } : undefined
  );
  // The deal form hides both loss fields on the parent layout unless the stage is lost, and the
  // platform drops them from a parent layout's required set: required there, nothing could fill them.
  const skipsLossFields =
    !childLayout &&
    (stageId === undefined ||
      resolveStage(funnelStages, funnelId, { id: stageId, name: "" })?.type !== DealStageType.LOST);

  return (childLayout?.schema ?? layout.schema).map((schemaField) => {
    // One lookup for both kinds: a custom field is keyed into the platform-name map by its esKey,
    // which is exactly what a CUSTOM layout row carries (FieldModel._buildMaps).
    const field = fieldModel.getByPlatformName(schemaField.field);
    // A system-required field's layout row stores `required: false` — the layout editor disables
    // the toggle — so the flag alone hides Company Name and lets a doomed create reach Confirm.
    const systemRequired =
      !!field?.isSystemRequired && !field.hasFeature(FieldFeature.ASSOCIATED_ENTITY);
    return {
      field,
      key: field?.name ?? schemaField.field,
      required:
        (schemaField.required || systemRequired) &&
        !(skipsLossFields && LOSS_PLATFORM_NAMES.includes(schemaField.field)),
      valued: VALUED_SCHEMA_FIELD_TYPES.includes(schemaField.fieldType),
    };
  });
};

// A custom field's value lives in a nested array addressed by esKey; every standard one is a flat
// key on the bag.
export const readCreateRecordValue = (
  values: Record<string, unknown>,
  { field, key }: Pick<CreateRecordSchemaField, "field" | "key">
): unknown => {
  if (!field || !isCustomField(field)) {
    return values[key];
  }
  const customFields = Array.isArray(values.customFields)
    ? (values.customFields as ChatCustomFieldValueRaw[])
    : [];
  return customFields.find((entry) => entry.esKey === key)?.value;
};

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || (typeof value === "string" && !value.trim());

// A custom field's value is always an array, so an array with no non-blank element is its "no
// value". The platform counts a custom field as filled by the same test.
export const isCreateRecordValueEmpty = (value: unknown): boolean =>
  Array.isArray(value) ? value.every(isBlank) : isBlank(value);

// A required entry this build cannot resolve to a field, or that carries no value at all, can
// never be satisfied by anything the rep does — so it must not veto Confirm. Same reasoning as an
// unloaded layout: we cannot judge it, and the platform's own `invalid` outcome is the backstop.
// This is also where the card and this predicate used to disagree: the card skips an unresolved
// entry silently, so a blocked Confirm had no visible cause anywhere on it.
// Required Groups and Files ARE judged, though the platform's create check skips both: the editor's
// form enforces them on Save, so a card that let them through would call ready a record its own
// editor refuses to save.
export const isCreateRecordRequirementJudgeable = ({
  field,
  valued,
}: CreateRecordSchemaField): boolean =>
  !!field && valued && field.platformName !== OWNER_PLATFORM_NAME;

// Returned rather than reduced to a boolean so callers can say WHICH field is missing — a rep
// staring at a disabled Create button otherwise has nothing to go on.
export const resolveCreateRecordMissingRequired = (
  schema: CreateRecordSchemaField[],
  values: Record<string, unknown>
): CreateRecordSchemaField[] =>
  schema.filter(
    (schemaField) =>
      schemaField.required &&
      isCreateRecordRequirementJudgeable(schemaField) &&
      isCreateRecordValueEmpty(readCreateRecordValue(values, schemaField))
  );

// Required entries deliberately skipped above. Surfaced only so a blocked card can say so out loud.
export const resolveCreateRecordUnjudgeableRequired = (
  schema: CreateRecordSchemaField[]
): CreateRecordSchemaField[] =>
  schema.filter(
    (schemaField) =>
      schemaField.required &&
      schemaField.field?.platformName !== OWNER_PLATFORM_NAME &&
      !isCreateRecordRequirementJudgeable(schemaField)
  );
